import { useUnit } from 'effector-react';
import { Fragment, type ReactNode, useEffect, useMemo, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import {
  $modal,
  modalClosed,
  modalOpened,
  type ModalConfirmSearchPayload,
  type ModalDrillPayload,
  type ModalState,
} from '@/model/modals';
import { toastSent } from '@/model/toasts';
import { navigated } from '@/model/router';
import { Button, Em, Arrow } from '@/components/primitives';

const fade = keyframes`from { opacity: 0; } to { opacity: 1; }`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: ${({ theme }) => theme.fx.overlay};
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 120;
  padding: ${({ theme }) => theme.spacing[6]};
  animation: ${fade} ${({ theme }) => theme.motion.duration.base} ${({ theme }) => theme.motion.easing.out};
`;

const Panel = styled.div<{ $wide?: boolean }>`
  width: 100%;
  max-width: ${({ $wide }) => ($wide ? '720px' : '480px')};
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.surface.elev};
  border: 1px solid ${({ theme }) => theme.line.line};
  border-radius: ${({ theme }) => theme.radii.md};
  box-shadow: ${({ theme }) => theme.shadows.sh3};
  padding: ${({ theme }) => `${theme.spacing[7]} ${theme.spacing[8]} ${theme.spacing[8]}`};
`;

const H5 = styled.h5`
  font-family: ${({ theme }) => theme.typography.fontFamilies.serif};
  font-weight: ${({ theme }) => theme.typography.fontWeights.regular};
  font-size: ${({ theme }) => theme.typography.fontSizes.hSm};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacings.tight};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
  font-variation-settings: 'opsz' 48;
`;

const P = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSizes.bodySm};
  color: ${({ theme }) => theme.ink[2]};
  line-height: ${({ theme }) => theme.typography.lineHeights.relaxed};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const Options = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const Opt = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  border: 1px solid ${({ theme }) => theme.line.line};
  border-radius: ${({ theme }) => theme.radii.sm};
  background: ${({ theme }) => theme.surface.bg};
  cursor: pointer;
  text-align: left;
  transition:
    border-color ${({ theme }) => theme.motion.duration.fast} ${({ theme }) => theme.motion.easing.out},
    background ${({ theme }) => theme.motion.duration.fast} ${({ theme }) => theme.motion.easing.out};

  &:hover {
    border-color: ${({ theme }) => theme.ink[3]};
    background: ${({ theme }) => theme.surface.elev};
  }

  .t {
    font-size: ${({ theme }) => theme.typography.fontSizes.ui};
    font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
    color: ${({ theme }) => theme.ink[1]};
  }
  .k {
    font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
    font-size: ${({ theme }) => theme.typography.fontSizes.monoSm};
    color: ${({ theme }) => theme.ink[3]};
    letter-spacing: ${({ theme }) => theme.typography.letterSpacings.wide};
  }
`;

const Foot = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-top: ${({ theme }) => theme.spacing[4]};
`;

const stop = (e: React.MouseEvent) => e.stopPropagation();

export const Modals = () => {
  const state = useUnit($modal);

  useEffect(() => {
    if (!state) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') modalClosed();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state]);

  if (!state) return null;

  return (
    <Backdrop onClick={() => modalClosed()}>
      <Panel onClick={stop} $wide={state.kind === 'drill-down' || state.kind === 'confirm-search'}>
        {state.kind === 'share' && <ShareBody />}
        {state.kind === 'example' && <ExampleBody />}
        {state.kind === 'drill-down' && <DrillDownBody payload={state.payload} />}
        {state.kind === 'confirm-search' && <ConfirmSearchBody payload={state.payload} />}
      </Panel>
    </Backdrop>
  );
};

type BodyProps = { children?: ReactNode };

const ShareBody = (_: BodyProps) => {
  const copy = () => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText('https://lume.design/r/LM-04218').catch(() => undefined);
    }
    modalClosed();
    toastSent('Ссылка скопирована');
  };

  return (
    <>
      <H5>
        Поделиться <Em>отчётом</Em>
      </H5>
      <P>
        Lume сгенерирует приватную ссылку с выбранным фрагментом отчёта. Вы сможете отозвать её в любой
        момент.
      </P>
      <Options>
        <Opt onClick={copy}>
          <span className="t">Приватная ссылка</span>
          <span className="k">⌘ · C</span>
        </Opt>
        <Opt
          onClick={() => {
            modalClosed();
            toastSent('Email ушёл');
          }}
        >
          <span className="t">Отправить на почту</span>
          <span className="k">↵</span>
        </Opt>
        <Opt
          onClick={() => {
            modalClosed();
            toastSent('QR открыт');
          }}
        >
          <span className="t">QR-код</span>
          <span className="k">Q</span>
        </Opt>
      </Options>
      <Foot>
        <Button $variant="ghost" $size="sm" onClick={() => modalClosed()}>
          Отмена
        </Button>
        <Button $size="sm" onClick={copy}>
          Скопировать ссылку
        </Button>
      </Foot>
    </>
  );
};

const ExampleBody = (_: BodyProps) => (
  <>
    <H5>
      Пример <Em>отчёта</Em>
    </H5>
    <P>
      Посмотрим на вымышленного продуктового дизайнера «Мария Петрова» — настоящий формат отчёта Lume. Можно
      пройти весь сценарий, не вводя своих данных.
    </P>
    <Foot>
      <Button $variant="ghost" $size="sm" onClick={() => modalClosed()}>
        Закрыть
      </Button>
      <Button
        $size="sm"
        onClick={() => {
          modalClosed();
          navigated('report');
        }}
      >
        Открыть пример <Arrow>→</Arrow>
      </Button>
    </Foot>
  </>
);

// ---- Drill-down (списки, поиск, пагинация) -------------------------------

const HeadRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const Subtitle = styled.span`
  font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
  font-size: ${({ theme }) => theme.typography.fontSizes.monoSm};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacings.wide};
  color: ${({ theme }) => theme.ink[3]};
  text-transform: uppercase;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  background: ${({ theme }) => theme.surface.bg};
  border: 1px solid ${({ theme }) => theme.line.line};
  border-radius: ${({ theme }) => theme.radii.sm};
  color: ${({ theme }) => theme.ink[1]};
  font-size: ${({ theme }) => theme.typography.fontSizes.ui};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
  transition: border-color ${({ theme }) => theme.motion.duration.fast}
    ${({ theme }) => theme.motion.easing.out};
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.tone.halo.base};
  }
  &::placeholder {
    color: ${({ theme }) => theme.ink[4]};
  }
`;

const ListBox = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.line.soft};
  border-radius: ${({ theme }) => theme.radii.sm};
  background: ${({ theme }) => theme.surface.bg};
`;

const rowStyles = css`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: ${({ theme }) => theme.spacing[4]};
  align-items: center;
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  border-bottom: 1px solid ${({ theme }) => theme.line.soft};
  text-decoration: none;
  color: inherit;

  &:last-child {
    border-bottom: none;
  }
`;

const ItemRow = styled.div`
  ${rowStyles}
`;

const ItemLink = styled.a`
  ${rowStyles}
  cursor: pointer;
  transition: background ${({ theme }) => theme.motion.duration.fast}
    ${({ theme }) => theme.motion.easing.out};
  &:hover {
    background: ${({ theme }) => theme.surface.elev};
  }
`;

const ItemPrimary = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.ui};
  color: ${({ theme }) => theme.ink[1]};
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: ${({ theme }) => theme.typography.lineHeights.snug};
`;

const ItemSecondary = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.bodySm};
  color: ${({ theme }) => theme.ink[3]};
  margin-top: 2px;
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: ${({ theme }) => theme.typography.lineHeights.snug};
`;

const ItemMeta = styled.span`
  font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
  font-size: ${({ theme }) => theme.typography.fontSizes.monoSm};
  color: ${({ theme }) => theme.ink[3]};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacings.wide};
  white-space: nowrap;
`;

const Empty = styled.p`
  padding: ${({ theme }) => theme.spacing[6]};
  text-align: center;
  color: ${({ theme }) => theme.ink[3]};
  font-size: ${({ theme }) => theme.typography.fontSizes.bodySm};
`;

const Pager = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-right: auto;
  font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
  font-size: ${({ theme }) => theme.typography.fontSizes.monoSm};
  color: ${({ theme }) => theme.ink[3]};
`;

const DrillDownBody = ({ payload }: { payload: ModalDrillPayload }) => {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return payload.items;
    return payload.items.filter((it) => {
      const hay = `${it.primary} ${it.secondary ?? ''} ${it.meta ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [payload.items, query]);

  const pageSize = payload.pageSize;
  const totalPages = pageSize ? Math.max(1, Math.ceil(filtered.length / pageSize)) : 1;
  const safePage = Math.min(page, totalPages - 1);
  const visible = pageSize ? filtered.slice(safePage * pageSize, (safePage + 1) * pageSize) : filtered;

  return (
    <>
      <HeadRow>
        <H5 style={{ marginBottom: 0 }}>{payload.title}</H5>
        {payload.subtitle && <Subtitle>{payload.subtitle}</Subtitle>}
      </HeadRow>
      {payload.searchable && (
        <SearchInput
          type="text"
          placeholder="Поиск по списку…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(0);
          }}
        />
      )}
      <ListBox>
        {visible.length === 0 ? (
          <Empty>{payload.emptyMessage ?? 'Ничего не найдено'}</Empty>
        ) : (
          visible.map((it) => {
            const inner = (
              <>
                <div>
                  <ItemPrimary>{it.primary}</ItemPrimary>
                  {it.secondary && <ItemSecondary>{it.secondary}</ItemSecondary>}
                </div>
                {it.meta && <ItemMeta>{it.meta}</ItemMeta>}
              </>
            );
            return it.href ? (
              <ItemLink key={it.id} href={it.href} target="_blank" rel="noreferrer noopener">
                {inner}
              </ItemLink>
            ) : (
              <ItemRow key={it.id}>{inner}</ItemRow>
            );
          })
        )}
      </ListBox>
      <Foot>
        {pageSize && filtered.length > pageSize && (
          <Pager>
            <Button
              $variant="ghost"
              $size="sm"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={safePage === 0}
            >
              ←
            </Button>
            <span>
              {safePage + 1} / {totalPages}
            </span>
            <Button
              $variant="ghost"
              $size="sm"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={safePage >= totalPages - 1}
            >
              →
            </Button>
          </Pager>
        )}
        <Button $variant="ghost" $size="sm" onClick={() => modalClosed()}>
          Закрыть
        </Button>
      </Foot>
    </>
  );
};

// === Confirm-search (3-сек гард + сводка введённых данных) ================

const ConfirmList = styled.dl`
  display: grid;
  grid-template-columns: minmax(140px, max-content) 1fr;
  gap: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[5]}`};
  margin: ${({ theme }) => `${theme.spacing[2]} 0 ${theme.spacing[6]}`};
  max-height: 50vh;
  overflow-y: auto;
  padding-right: ${({ theme }) => theme.spacing[2]};
`;

const ConfirmLabel = styled.dt`
  font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
  font-size: ${({ theme }) => theme.typography.fontSizes.monoSm};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacings.wide};
  text-transform: uppercase;
  color: ${({ theme }) => theme.ink[3]};
  align-self: start;
  margin: 0;
`;

const ConfirmValue = styled.dd`
  font-size: ${({ theme }) => theme.typography.fontSizes.ui};
  color: ${({ theme }) => theme.ink[1]};
  margin: 0;
  line-height: ${({ theme }) => theme.typography.lineHeights.snug};
  overflow-wrap: anywhere;
  word-break: break-word;

  &.muted {
    color: ${({ theme }) => theme.ink[4]};
  }
`;

const CONFIRM_GUARD_SEC = 3;

const ConfirmSearchBody = ({ payload }: { payload: ModalConfirmSearchPayload }) => {
  const [secondsLeft, setSecondsLeft] = useState(CONFIRM_GUARD_SEC);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const t = window.setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => window.clearTimeout(t);
  }, [secondsLeft]);

  const ready = secondsLeft <= 0;

  return (
    <>
      <H5>
        Запустить <Em>поиск</Em>?
      </H5>
      <P>
        Pipeline нельзя остановить после старта — workers продолжат тратить токены даже после перезагрузки
        страницы. Проверьте данные ниже. Если что-то нужно исправить — закройте окно и вернитесь к форме.
      </P>
      <ConfirmList>
        {payload.rows.map((row) => {
          const empty = !row.value || row.value.trim() === '';
          return (
            <Fragment key={row.label}>
              <ConfirmLabel>{row.label}</ConfirmLabel>
              <ConfirmValue className={empty ? 'muted' : undefined}>{empty ? '—' : row.value}</ConfirmValue>
            </Fragment>
          );
        })}
      </ConfirmList>
      <Foot>
        <Button $variant="ghost" $size="sm" onClick={() => modalClosed()}>
          Назад к форме
        </Button>
        <Button
          $size="sm"
          disabled={!ready}
          onClick={() => {
            modalClosed();
            payload.onConfirm();
          }}
        >
          {ready ? (
            <>
              Запустить <Arrow>→</Arrow>
            </>
          ) : (
            `Подождите ${secondsLeft} сек.`
          )}
        </Button>
      </Foot>
    </>
  );
};

export const openModal = (state: Exclude<ModalState, null>) => modalOpened(state);
